import { Component, Property, TextComponent } from '@wonderlandengine/api';
import { ProgressionSystem } from './progression-system.js';
import { Arena } from './net/arena.js';

// PERFORMANCE: Cache static rank strings to avoid Garbage Collection (GC) overhead
const CACHED_RANKS = [
    '[1st] ', '[2nd] ', '[3rd] ', '[4.]  ', '[5.]  ', 
    '[6.]  ', '[7.]  ', '[8.]  ', '[9.]  ', '[10.] '
];

// PERFORMANCE: Pre-allocate text buffers to prevent GC spikes during text reconstruction
// This completely eliminates array and string reallocation during leaderboard refreshes
const MAX_PLAYERS = 10;
const _mainTextBuffer = new Array(MAX_PLAYERS * 3 + 3);
const _levelTextBuffer = new Array(MAX_PLAYERS * 3 + 3);

export class Leaderboard extends Component {
    static TypeName = 'leaderboard';
    static Properties = {
        levelTextObject: Property.object()
    };

    /**
     * Initialize the leaderboard component
     */
    start() {
        // PERFORMANCE: Cache component references locally to avoid querying across WASM boundary
        this.textLeaderboard = this.object.getComponent(TextComponent);
        
        this.textLevels = null;
        if (this.levelTextObject) {
            this.textLevels = this.levelTextObject.getComponent(TextComponent);
        }

        // Kick off the leaderboard load as soon as REST auth resolves.
        // `Arena.ready` always resolves (never rejects), so no .catch needed.
        this._loadAborted = false;
        this.showOfflineMessage();
        Arena.ready.then(() => {
            if (this._loadAborted) return;
            this.loadLeaderboard();
        });

        // PERFORMANCE: Cache bound functions to avoid allocation on event listener add/remove
        this._boundOnXRSessionEnd = this.onXRSessionEnd.bind(this);
        this._boundOnDesktopSessionEnd = this.onDesktopSessionEnd.bind(this);

        if (this.engine && this.engine.onXRSessionEnd) {
            this.engine.onXRSessionEnd.add(this._boundOnXRSessionEnd);
        }
        window.addEventListener('desktop-session-end', this._boundOnDesktopSessionEnd);
    }

    onDesktopSessionEnd() {
        this._resetSession();
    }

    onXRSessionEnd() {
        this._resetSession();
    }

    _resetSession() {
        // Reload from server; offline view will be replaced if it succeeds.
        this.showOfflineMessage();
        Arena.ready.then(() => {
            if (!this._loadAborted) this.loadLeaderboard();
        });
    }

    onDestroy() {
        this._loadAborted = true;
        if (this._boundOnXRSessionEnd && this.engine && this.engine.onXRSessionEnd) {
            this.engine.onXRSessionEnd.remove(this._boundOnXRSessionEnd);
        }
        if (this._boundOnDesktopSessionEnd) {
            window.removeEventListener('desktop-session-end', this._boundOnDesktopSessionEnd);
        }
    }

    showOfflineMessage() {
        this.textLeaderboard.text = this.formatHeader() +
            "\n" +
            "    [*] Connecting...\n" +
            "\n" +
            this.formatFooter();

        if (this.textLevels) {
            this.textLevels.text = "";
        }
    }

    formatHeader() {
        return "ARENAWARS LEADERBOARD\n";
    }

    formatFooter() {
        return "";
    }

    async loadLeaderboard() {
        try {
            // PERFORMANCE: Use async/await to avoid callback chain allocations
            const players = await Arena.leaderboard.get("arenawars-leaderboard", MAX_PLAYERS, 1);
            
            if (players.length === 0) {
                this.showEmptyLeaderboard();
                return;
            }

            this.displayLeaderboard(players);
        } catch (err) {
            console.error('Arena Wars Leaderboard error:', err);
            this.showErrorMessage();
        }
    }

    showEmptyLeaderboard() {
        this.textLeaderboard.text = this.formatHeader() + 
            "\n\n" +
            "    [!] No champions yet!\n" +
            "        Be the first warrior to\n" +
            "        claim your place in glory!\n" +
            "\n" +
            this.formatFooter();
        
        if (this.textLevels) {
            this.textLevels.text = "";
        }
    }

    showErrorMessage() {
        this.textLeaderboard.text = this.formatHeader() + 
            "\n\n" +
            "    [!] Connection Error\n" +
            "        Unable to load rankings\n" +
            "        Please try again later...\n" +
            "\n" +
            this.formatFooter();
        
        if (this.textLevels) {
            this.textLevels.text = "";
        }
    }

    displayLeaderboard(players) {
        if (this.textLevels) {
            this.displayLeaderboardDualText(players);
        } else {
            this.displayLeaderboardSingleText(players);
        }
    }

    displayLeaderboardDualText(players) {
        // PERFORMANCE: Use pre-allocated buffers to completely avoid string concatenation 
        // intermediate allocations and GC pauses in loops.
        let mainIdx = 0;
        let levelIdx = 0;
        
        _mainTextBuffer[mainIdx++] = this.formatHeader();
        _mainTextBuffer[mainIdx++] = "\n\n";
        
        _levelTextBuffer[levelIdx++] = "\n\n\n";
        
        const len = players.length;
        for (let i = 0; i < len; i++) {
            const player = players[i];
            const combinedScore = player.score;
            
            const prestige = Math.floor(combinedScore / 1000);
            const level = combinedScore % 1000;
            
            const rankDisplay = CACHED_RANKS[i] || `[${i + 1}.] `;
            const playerName = this.formatPlayerName(player.user);
            
            _mainTextBuffer[mainIdx++] = rankDisplay;
            _mainTextBuffer[mainIdx++] = playerName;
            _mainTextBuffer[mainIdx++] = "\n";
            
            const displayLevel = prestige > 0 ? 
                `${ProgressionSystem.getPrestigeName(prestige)} ${level}` : 
                `Level ${level}`;
                
            _levelTextBuffer[levelIdx++] = displayLevel;
            _levelTextBuffer[levelIdx++] = "\n";
            
            if (i < len - 1) {
                _mainTextBuffer[mainIdx++] = "\n";
                _levelTextBuffer[levelIdx++] = "\n";
            }
        }

        _mainTextBuffer[mainIdx++] = "\n";
        _mainTextBuffer[mainIdx++] = this.formatFooter();
        _levelTextBuffer[levelIdx++] = "\n";
        
        // Manual join to avoid slice() array allocation
        let finalMainText = "";
        for (let i = 0; i < mainIdx; i++) finalMainText += _mainTextBuffer[i];
        
        let finalLevelText = "";
        for (let i = 0; i < levelIdx; i++) finalLevelText += _levelTextBuffer[i];

        // Only one cross-WASM string assignment
        this.textLeaderboard.text = finalMainText;
        this.textLevels.text = finalLevelText;
    }

    displayLeaderboardSingleText(players) {
        let mainIdx = 0;
        
        _mainTextBuffer[mainIdx++] = this.formatHeader();
        _mainTextBuffer[mainIdx++] = "\n\n";
        
        const len = players.length;
        for (let i = 0; i < len; i++) {
            const player = players[i];
            const combinedScore = player.score;
            
            const prestige = Math.floor(combinedScore / 1000);
            const level = combinedScore % 1000;
            
            const rankDisplay = CACHED_RANKS[i] || `[${i + 1}.] `;
            const playerName = this.formatPlayerName(player.user);
            
            const displayLevel = prestige > 0 ? 
                `${ProgressionSystem.getPrestigeName(prestige)} ${level}` : 
                `Level ${level}`;
            
            _mainTextBuffer[mainIdx++] = rankDisplay;
            _mainTextBuffer[mainIdx++] = playerName;
            _mainTextBuffer[mainIdx++] = "\n                ";
            _mainTextBuffer[mainIdx++] = displayLevel;
            _mainTextBuffer[mainIdx++] = "\n";
            
            if (i < len - 1) {
                _mainTextBuffer[mainIdx++] = "\n";
            }
        }

        _mainTextBuffer[mainIdx++] = "\n";
        _mainTextBuffer[mainIdx++] = this.formatFooter();

        // Manual join to avoid slice() array allocation
        let finalMainText = "";
        for (let i = 0; i < mainIdx; i++) finalMainText += _mainTextBuffer[i];
        
        this.textLeaderboard.text = finalMainText;
    }

    formatPlayerName(name) {
        const maxLength = 18;
        if (name.length > maxLength) {
            return name.substring(0, maxLength - 3) + '...';
        }
        return name.padEnd(maxLength);
    }

    /**
     * Update player's leaderboard entry
     * PERFORMANCE: Refactored to async/await to avoid multiple function closure allocations 
     * in the promise chain, reducing GC spikes.
     */
    async updateLeaderboard(totalLevel, playerName = null, prestige = 0) {
        const combinedScore = (prestige * 1000) + totalLevel;

        // Wait for REST auth before posting (no-op if already ready).
        await Arena.ready;

        try {
            const name = playerName || await Arena.user.getName();
            const myScores = await Arena.leaderboard.getMy("arenawars-leaderboard", 1);
            
            // PERFORMANCE: Use standard for-loop instead of .find() to avoid closure allocation
            let existingEntry = null;
            if (myScores && myScores.length > 0) {
                for (let i = 0; i < myScores.length; i++) {
                    if (myScores[i].user === name) {
                        existingEntry = myScores[i];
                        break;
                    }
                }
            }
            
            if (!existingEntry || combinedScore > existingEntry.score) {
                await Arena.leaderboard.postScore("arenawars-leaderboard", combinedScore);
            }
            
            this.loadLeaderboard();
        } catch (err) {
            console.error('[!] Leaderboard update error:', err);
            this.showErrorMessage();
        }
    }

    showOfflineProgress(level, prestige = 0) {
        const displayLevel = prestige > 0 ?
            `${ProgressionSystem.getPrestigeName(prestige)} ${level}` :
            `Level ${level}`;

        this.textLeaderboard.text = this.formatHeader() +
            "\n\n    [*] Offline mode\n\n    [>] Your Progress:\n        " +
            displayLevel + "\n\n" +
            this.formatFooter();
    }
}
